import {
    AccountInfo,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from '@azure/msal-browser'
import { msalInstance } from './authConfig'

const env = (<any>window)['appenv']

// export enum AuthorizationRoles {
//     AllRead = 'All.Read',
//     AllReadWrite = 'All.ReadWrite',
//     CrossTrackReadWrite = 'Alarms.crosstrack.ReadWrite',
//     SpeedReadWrite = 'Alarms.speed.ReadWrite',
//     NoPlanReadWrite = 'Alarms.no-plan.ReadWrite',
//     StoppageReadWrite = 'Alarms.stoppage.ReadWrite',
// }

export const AuthService = new (class extends EventTarget {
    constructor() {
        super()

        if (env.authRequired) {
            this._publicClientApplication = msalInstance

            this._initializeMsal()
        }
    }

    async getAccessToken(scope: string) {
        try {
            if (!this._isLoggedIn) {
                await new Promise((resolve) => {
                    this.addEventListener('logged-in', resolve, { once: true })
                })
            }

            const tokenRequest = {
                scopes: [scope],
                account: this.getLocalAccount() || undefined,
            }

            try {
                const authResponse =
                    await this._publicClientApplication!.acquireTokenSilent(
                        tokenRequest,
                    )

                if (!authResponse.accessToken) {
                    throw new Error(
                        'auth-service: accessToken was unexpectedly invalid',
                    )
                }

                return authResponse.accessToken
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    const refreshedToken = await this.refreshToken()
                    if (refreshedToken) {
                        return refreshedToken
                    }

                    // if refresh failed, the refreshToken method already dispatched login-required
                    this._isLoggedIn = false
                    return null
                } else {
                    console.error('Token acquisition error:', error)
                    throw error
                }
            }
        } catch (error) {
            console.error('Error getting access token:', error)
            throw error
        }
    }

    getLocalAccount() {
        const activeAccount = this._publicClientApplication!.getActiveAccount()
        const accounts = this._publicClientApplication!.getAllAccounts()

        if (!activeAccount && accounts.length === 0) {
            return null
        }

        return activeAccount || accounts[0]
    }

    get isRedirecting() {
        return this._isRedirecting
    }

    get isLoggedIn() {
        return this._isLoggedIn
    }

    startRedirectFlow() {
        this._isRedirecting = true
        setTimeout(() => this.doAuth(), 0)
    }

    getSessionUsername() {
        return sessionStorage.getItem('username')
    }

    getSessionVesselIMO() {
        return sessionStorage.getItem('vesselIMO')
    }

    setSessionVesselIMO(imo: string) {
        sessionStorage.setItem('vesselIMO', imo);
        window.dispatchEvent(new Event('VesselIMO'));
    }

    extractVesselIMO(username: string): string | null {
        const imoPattern = /\d{7}/ // imo is 7 d
        const match = username.match(imoPattern)
        return match ? match[0] : null
    }

    async doAuth() {
        this._isRedirecting = true   
        // checking for existing MSAL interaction in session storage
        if (sessionStorage.getItem('msal.interaction.status') === 'interaction_in_progress') {
            return
        }

        // always redirecting to login - session validation is handled in refreshToken()
        try {
            await this._publicClientApplication!.loginRedirect({
                scopes: this._scopes,
            })
        } catch (error) {
            console.error('Error during loginRedirect:', error)
        }
    }

    private async _initializeMsal() {
        try {
            this._handleRedirectResponse()
        } catch (error) {
            console.error('Error during MSAL initialization:', error)
            this.dispatchEvent(new Event('login-required'))
        }
    }

    private setSessionDetails(account: AccountInfo) {
        const username = account.username
        if (username) {
            sessionStorage.setItem('username', username)
            const imo = this.extractVesselIMO(username)
            if (imo) {
                sessionStorage.setItem('isVesselUser', 'Yes');
                // for reproducing only vesselUser scenario
                // If condition satidfy only for test vessel user
                // test vessel user 1234567scvessel@maersk.onmicrosoft.com ie dummy imo 1234567
                if (imo === '1234567') { // Test vessel IMO
                    this.setSessionVesselIMO('9509140'); // this imo can be replaced to any valid production imo to verify production data
                } else {
                    this.setSessionVesselIMO(imo); // will set actual imo
                }
            } else {
                sessionStorage.setItem('isVesselUser', 'No');
            }
        }
    }

    async _handleRedirectResponse() {
        try {
            const tokenResponse =
                await this._publicClientApplication!.handleRedirectPromise()
            if (tokenResponse) {
                const account = tokenResponse.account
                if (account) {
                    this._publicClientApplication!.setActiveAccount(account)
                    this.setSessionDetails(account)
                    this._isLoggedIn = true
                    this.dispatchEvent(new Event('logged-in'))
                }
            } else {
                console.log('No token response, checking existing accounts...')
                const accounts = this._publicClientApplication!.getAllAccounts()
                if (accounts.length > 0) {
                    const account = accounts[0]
                    this._publicClientApplication!.setActiveAccount(account)
                    this.setSessionDetails(account)
                    this._isLoggedIn = true
                    this.dispatchEvent(new Event('logged-in'))
                } else {
                    this.dispatchEvent(new Event('login-required'))
                }
            }
        } catch (err) {
            console.error('Error from handleRedirectPromise:', err)
            this.dispatchEvent(new Event('login-required'))
        } finally {
            // just making sure - clear interaction status manually in case MSAL doesn't
            sessionStorage.removeItem('msal.interaction.status')
        }
    }

    async logout() {
        try {
            if (!this._publicClientApplication) {
                console.warn('MSAL instance not initialized')
                return
            }
            sessionStorage.removeItem('username')
            sessionStorage.removeItem('vesselIMO')

            await this._publicClientApplication.logoutRedirect()
        } catch (error) {
            console.error('Error during logout:', error)
        }
    }

    async refreshToken() {
        try {
            const account = this.getLocalAccount()
            if (!account) {
                this.dispatchEvent(new Event('login-required'))
                return null
            }

            // try with minimal scope first to verify session validity
            try {
                const minimalScopeRequest = {
                    scopes: ['openid'], // Minimal scope to test session
                    account: account
                }
                
                // first check if we have a valid session at all
                await this._publicClientApplication!.acquireTokenSilent(minimalScopeRequest)
                
                // if we get here, try to get the actual scopes we need
                const fullScopeRequest = {
                    scopes: this._scopes,
                    account: account,
                    forceRefresh: true,
                }
                
                const response = await this._publicClientApplication!.acquireTokenSilent(fullScopeRequest)
                
                this._isLoggedIn = true
                this.dispatchEvent(new Event('token-refreshed'))
                
                return response.accessToken
            } catch (error) {            
                // If refresh fails due to refresh token expiration or other reasons
                // we need to trigger interactive login
                if (error instanceof InteractionRequiredAuthError) {
                    this._isLoggedIn = false
                    this.dispatchEvent(new Event('login-required'))
                }
                
                return null
            }
        } catch (error) {
            console.error('Error in refresh flow:', error)
            this._isLoggedIn = false
            this.dispatchEvent(new Event('login-required'))
            return null
        }
    }

    private _scopes: string[] = [`${env.clientId}/.default`]
    private _isLoggedIn = false
    private _publicClientApplication: PublicClientApplication | null = null
    private _isRedirecting = false
})()